/**
 * Core types for the My First Nightshift engine.
 *
 * Conventions
 * - State is plain JSON (no Map/Set/class) so it can be structuredClone'd,
 *   persisted, and hashed.
 * - Everything hidden from players lives in `SessionState.backs`. Validators
 *   receive `PublicState` (state without `backs`) so they cannot read it.
 * - All iteration uses explicit orders (ROLE_ORDER, bed index), never object
 *   key order.
 */

import type { ActionCommand } from './commands.ts';

// ───────────────────────────── enumerations ─────────────────────────────

export type Colour = 'GREEN' | 'AMBER' | 'RED';
export const COLOUR_ORDER: Colour[] = ['GREEN', 'AMBER', 'RED'];

export type Role = 'EM' | 'SURG' | 'PHYS' | 'PSYCH' | 'ICU';
export const ROLE_ORDER: Role[] = ['EM', 'SURG', 'PHYS', 'PSYCH', 'ICU'];

export type Owner = Role | 'ANY';

export type TestKind = 'ASSESS' | 'OPINION_PHYS' | 'OPINION_PSYCH' | 'OPINION_SURG' | 'SCAN';
export const TEST_ORDER: TestKind[] = ['ASSESS', 'OPINION_PHYS', 'OPINION_PSYCH', 'OPINION_SURG', 'SCAN'];

export type RouteCode = 'S' | 'M' | 'P' | 'G' | 'X'; // X = Exploratory Polishing pseudo-route
export type Risk = 'NONE' | 'LOW' | 'HIGH' | 'CRITICAL';
export type Rate = 'LOW' | 'MED' | 'HIGH';
export type BedKind = 'bay' | 'SW' | 'MW' | 'QW' | 'ICU';
export type Box = 50 | 70 | 90 | 100; // 100 = CONFIRMED (✓)
export const CONFIRMED: Box = 100;

export type StepKind =
  | 'STABILISE'
  | 'TREAT'
  | 'REASSURE'
  | 'SCAN'
  | 'THEATRE'
  | 'ICU'
  | 'STEP_DOWN'
  | 'OBSERVE'
  | 'DISCHARGE';

export type Phase =
  | 'SETUP'
  | 'ARRIVALS'
  | 'HUDDLE'
  | 'ACT'
  | 'TICK_OPEN'
  | 'TICK_CLOSE'
  | 'LOG'
  | 'HANDOVER'
  | 'DEBRIEF'
  | 'ENDED';

export type PresetKey = 'ORIENTATION' | 'MIDYEAR' | 'EXIT';
export type PacingKey = 'STANDARD' | 'NINETY' | 'TEACHING' | 'CUSTOM';
export type LayerKey = 'staff' | 'vents' | 'blood';
export type EarlyLine = 'TOLERATES' | 'BOUNCE_2' | 'BOUNCE_1';

export type PatientId = string;
export type EventKey = string; // 'E1'..'E45'
export type ObjectiveKey = string;

// ───────────────────────────── content ─────────────────────────────

export interface Step {
  kind: StepKind;
  label: string;
  by: Owner[];
  warn?: boolean;
  inICU?: boolean;
  bed?: BedKind | 'any';
  blood?: number;
}

export interface Route {
  code: RouteCode;
  steps: Step[];
  turns: number;
  uses: string[];
  riskIfWrong: Risk;
  complRate: Rate | null;
}

export interface Diagnosis {
  key: string;
  name: string;
  colour: Colour;
  differentials: string[];
  definitive: TestKind | null;
  ruleOuts: Record<string, TestKind>;
  routes: Route[];
  vent?: boolean;
  page: number;
  special?: 'newborn' | 'vip' | 'choir' | 'exit-only';
  notes?: string;
  /** Psychiatry-only diagnosis family (removed at four-seat tables). */
  psychOnly?: boolean;
  /** Name printed on the card front if different from `name` (e.g. "Very Hairy"). */
  frontName?: string;
}

// ───────────────────────────── config ─────────────────────────────

export interface Pacing {
  key: PacingKey;
  /** Game turns mapped onto script turns (1..10). The last entry is the handover turn. */
  scriptTurns: number[];
  arrivalsMs: number;
  huddleMs: number;
  actMs: number;
  tickMs: number;
  logMs: number;
  handoverMs: number;
}

export interface SessionConfig {
  name: string;
  preset: PresetKey;
  tables: number;
  pacing: Pacing;
  layers: Record<LayerKey, boolean>;
  ringOrder: number[];
  seed: string;
  /** Test-only time compression factor (1 = real time). */
  clockScale?: number;
}

// ───────────────────────────── patients ─────────────────────────────

export type TestResult =
  | { kind: 'RULES_OUT'; x: string }
  | { kind: 'CONFIRMED' }
  | { kind: 'INCONCLUSIVE' }
  | { kind: 'FLIP'; x: string };

export type PatientStatus =
  | 'DECK'
  | 'ACTIVE'
  | 'INCOMING'
  | 'DISCHARGED'
  | 'DIED'
  | 'BOUNCE_PENDING'
  | 'HANDED_OVER'
  | 'TURNED_AWAY';

export type Location =
  | { kind: 'bed'; index: number }
  | { kind: 'corridor' }
  | { kind: 'none' };

export interface RevealedLine {
  line: TestKind | 'TRUTH' | 'COMPLICATION' | 'EARLY' | 'BIAS';
  text: string;
  turn: number;
}

export interface RouteProgress {
  code: RouteCode;
  next: number;
  startedTurn: number;
  /** Diagnosis the route belongs to (believedKey when started). */
  diag: string;
}

export interface Patient {
  id: PatientId;
  table: number;
  seat: Role | null;
  loc: Location;
  status: PatientStatus;
  name: string;
  flavour: string;
  colour: Colour;
  /** Provisional diagnosis key as printed (never changes). */
  provisionalKey: string;
  /** The team's working diagnosis: provisional, or the truth once flipped. */
  believedKey: string;
  printedBox: Box;
  box: Box;
  ruledOut: string[];
  needsStabilise: boolean;
  route: RouteProgress | null;
  icuStreak: number;
  arrivedTurn: number;
  attendedTurn: number;
  revealed: RevealedLine[];
  /** Set when the patient arrived on its current list by referral; cleared by the next treatment step. */
  referral: { from: Role; turn: number } | null;
  /** True clock is public to the holder (D-32); mirrored from the back for convenience. */
  ticks: number;
  flags: {
    cluster?: boolean;
    clusterTrueBox?: Box;
    bouncedBack?: boolean;
    special?: 'sprocket' | 'vip' | 'choir' | 'newborn' | 'regional';
    treated?: boolean;
    lastSeat?: Role;
    divertedFrom?: number;
    ventHeld?: boolean;
    decree?: boolean;
    pinnedStaff?: boolean;
    unstaffed?: boolean;
    compInICU?: boolean;
    overflowDelayed?: boolean;
    dischargeNotBeforeTurn?: number;
  };
  /** Per-turn scratch, cleared at LOG. */
  turnScratch: {
    warnRoute?: RouteCode;
    warnBlood?: boolean;
    earlySkipped?: number;
    earlyBy?: Role | 'LOCUM';
    actedBy?: Role[];
    deteriorated?: boolean;
    supports?: number;
    stepsDone: number;
  };
}

/** Hidden half of a patient: never projected to players. */
export interface Back {
  family: string;
  cardCode: string;
  truthKey: string;
  results: Partial<Record<TestKind, TestResult>>;
  complication: RouteCode[];
  early: EarlyLine;
  bounceBack: boolean;
  barnacle: boolean;
  bias: string[];
  trap: boolean;
  labelled: boolean;
  flavourKind: 'neutral' | 'hint' | 'herring';
  drawn: boolean;
  preload: boolean;
}

// ───────────────────────────── table & seats ─────────────────────────────

export interface SeatStats {
  actionsByTurn: Record<number, number>;
  actionsLost: number;
  discharges: number;
  referralsOut: number;
  unconfirmedTreats: number;
  unconfirmedWrongHighCrit: number;
  confirmedBeforeTreat: number;
  vipCompleted: boolean;
  deteriorationsOnList: number;
  deathsByTurn: Record<number, number>;
  theatreSteps: number;
  anaesthetics: number;
  helpGiven: number;
  helpReceived: number;
  referralsIn: number;
  referralsToCorridor: number;
  earlyPassed: number;
  earlyBounced: number;
  patientsAtStartOfTurn: Record<number, number>;
}

export interface SeatState {
  role: Role;
  name: string | null;
  bot: boolean;
  objective: ObjectiveKey | null;
  actions: { available: number; spent: number };
  beds: (PatientId | null)[];
  corridor: PatientId[];
  lastActedOn: PatientId | null;
  freeAssessUsedTurn: number;
  e22UsedTurn: number;
  staff: number;
  stats: SeatStats;
}

export interface Interrupt {
  id: string;
  seat: Role;
  /** E29: the holder reads one patient's definitive line free on resolving. */
  bonus?: boolean;
  /** E44: the patient the family is worried about (reviewed on resolving; ticks if still unresolved at the Tick). */
  patient?: PatientId;
  event: EventKey;
  title: string;
  state: 'NEW' | 'PARKED' | 'ESCALATED' | 'RESOLVED';
  age: number;
  dealtTurn: number;
}

export interface Prompt {
  id: string;
  seat: Role;
  kind: 'E17' | 'E23' | 'E24' | 'E29_PICK';
  patient?: PatientId;
  options?: string[];
  data?: Record<string, unknown>;
  phase: Phase;
}

export interface PendingOutcome {
  kind: 'COMPLICATION' | 'EARLY';
  patient: PatientId;
  seat: Role;
  route?: RouteCode;
  promptId?: string;
  skipped?: number;
  blood?: boolean;
  fails: boolean;
}

export interface Locum {
  id: string;
  arrivesTurn: number;
  untilTurn: number | null;
  actionsLeft: number;
  booking?: number;
}

export interface Booking {
  kind: 'LOCUM' | 'THEATRE' | 'BED_MEETING';
  bookedTurn: number;
  from: number;
  until: number | null;
  used: number;
  request: 'PLANNED' | 'CRISIS';
}

export interface Dashboard {
  overflowDelays: number;
  saved: number;
  harmed: number;
  died: number;
  diverted: number;
  accepted: number;
  early: number;
  bounceBacks: number;
  handedOver: number;
  scans: number;
  theatre: number;
  icuBedTurns: number;
  calls: number;
  objectives: number;
  objectivesDealt: number;
  unstaffedPatientTurns: number;
  bloodUsed: number;
  ventShortTurns: number;
  idleCapacity: number;
}

export interface TheatreState {
  booking: { patient: PatientId; surgeon: Role } | null;
  anaesthetic: Role | null;
  used: boolean;
  ventTaken: boolean;
}

export interface TableBoard {
  scannerUsed: number;
  /** Who used each Scanner box this turn (index = box). */
  scannerUsedBy: (Role | null)[];
  theatre: TheatreState;
  secondTheatre: { used: boolean; ventTaken: boolean } | null;
  allocations: { scanner: (Role | null)[]; theatre: Role | null };
  divert: boolean;
  bedCounts: Record<Role, { used: number; cap: number; corridor: number }>;
  staffLoans: { from: Role; to: Role; n: number; fromTurn: number; untilTurn: number }[];
  /** Temporary staff removals from events (E36, E38), restored after `until`. */
  staffAdjust: { role: Role; n: number; until: number }[];
  vents: { total: number; recalledUntil: number; recalled: number } | null;
  blood: { units: number; orders: { arriveTurn: number; units: number }[]; used: number; wasted: number } | null;
}

export interface DraftAction {
  id: string;
  revision: number;
  cmd: ActionCommand;
  intent: { diagnosis: string; route: RouteCode | null; step: number | null } | null;
}

export interface DraftPlan {
  turn: number;
  revision: number;
  ready: boolean;
  actions: DraftAction[];
}

export interface ReferralConsent {
  sender: Role;
  receiver: Role;
  actionId: string;
  offerRevision: number;
  mode: 'BED' | 'OVERFLOW';
}

export interface ActionOutcome {
  seat: Role;
  actionId: string;
  status: 'APPLIED' | 'BLOCKED' | 'CANCELLED' | 'DELAYED';
  reason: string;
}

export interface TurnReceipt {
  id: string;
  turn: number;
  boundary: 'COMMIT' | 'RELEASE';
  events: DomainEvent[];
  outcomes: ActionOutcome[];
}

export interface TableState {
  no: number;
  fiveSeat: boolean;
  seats: Partial<Record<Role, SeatState>>;
  drafts: Partial<Record<Role, DraftPlan>>;
  consents: ReferralConsent[];
  receipts: TurnReceipt[];
  tests: { patient: PatientId; test: TestKind; by: Role | 'LOCUM'; diagnosis: string; routeStep: { diag: string; code: RouteCode; next: number } | null }[];
  pendingTransfers: { patient: PatientId; from: Role; to: Role; turn: number }[];
  overflow: Partial<Record<Role, { ratio: number; probability: number; checked: boolean }>>;
  lead: Role;
  compendiumHolder: Role;
  deck: PatientId[];
  bounceSlot: { id: PatientId; returnTurn: number }[];
  board: TableBoard;
  openCards: { patient: PatientId; to: Role; from: Role }[];
  /** 0700 handovers done (patient ids). */
  handedOver: PatientId[];
  divertNotice: { fromTable: number; count: number; turn: number }[];
  interrupts: Interrupt[];
  prompts: Prompt[];
  pending: PendingOutcome[];
  locums: Locum[];
  bookings: Booking[];
  callsForHelp: number;
  timeOut: { used: boolean; turn: number | null };
  dashboard: Dashboard;
  trapList: PatientId[];
  disputes: { turn: number; note: string }[];
  /** Board counters as last published at LOG. */
  published: { saved: number; harmed: number; died: number };
  effects: {
    physicianAwayUntil: number;
    /** E45: one seat has one fewer action through `until`. */
    sickLeave: { role: Role; until: number } | null;
    cover: Role | null;
    decree: 'COMPLY' | 'CHALLENGE' | null;
    clusterLeft: number;
    leadPenalty: { role: Role; turn: number } | null;
    freeAssessE16Turn: number;
    /** E7 actor keys take precedence; otherwise meeting holder keys share this turn's reset. */
    e7FirstDischargeUsed: Role[];
    fullMoonTurn: number;
  };
  tableStats: {
    firstOverflowTurn: number | null;
    scansTotal: number;
    divertTurns: number;
    callTurns: number[];
    stepDownLate: number;
    stepDownOnTime: number;
    stepDownWaiting: Record<PatientId, number>;
  };
}

// ───────────────────────────── session ─────────────────────────────

export interface ScriptTurn {
  gameTurn: number;
  scriptTurn: number;
  arrivals: number;
  events: EventKey[];
  handover: boolean;
}

export interface ActiveEvent {
  key: EventKey;
  turn: number;
  until: number;
}

export interface ClockState {
  turn: number;
  phase: Phase;
  paused: boolean;
  hold: boolean;
  phaseEndsAt: number | null;
  remainingMs: number | null;
  lastTurn: number;
  startedAt: number | null;
}

export interface SessionState {
  v: 1;
  config: SessionConfig;
  seq: number;
  rng: number;
  counters: { patient: number; handover: number; interrupt: number; prompt: number; misc: number };
  clock: ClockState;
  script: ScriptTurn[];
  room: {
    queuedInjects: EventKey[];
    activeEvents: ActiveEvent[];
    thisTurnEvents: EventKey[];
    e35Held: boolean;
    e35Applied: boolean;
    broadcast: string | null;
    spareReds: PatientId[];
  };
  tables: TableState[];
  patients: Record<PatientId, Patient>;
  backs: Record<PatientId, Back>;
}

/** Everything a validator is allowed to see. */
export type PublicState = Omit<SessionState, 'backs'>;

// ───────────────────────────── domain events ─────────────────────────────

export type Audience = 'faculty' | 'table' | `seat:${Role}`;

export interface DomainEvent {
  type: string;
  table: number | null;
  seat?: Role | null;
  patient?: PatientId | null;
  turn: number;
  phase: Phase;
  severity: number;
  tag?: string;
  text: string;
  audience: Audience[];
  data?: Record<string, unknown>;
}
